import {
  AfterViewInit,
  Component,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  TemplateRef,
  viewChild
} from '@angular/core';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTableDataSource,
  MatTableModule
} from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { GermanPaginatorIntl } from '../../../services/paginator/german-paginator-intl';
import { GenericTableTestIds } from './generic-table.test-ids';

export interface TableColumn<T> {
  key: keyof T & string;
  label: string;
  type?: 'text' | 'date' | 'number' | 'checkbox' | 'custom';
  sortable?: boolean;
  template?: TemplateRef<{ $implicit: T }>;
}

@Component({
  selector: 'app-generic-table',
  imports: [
    MatTableModule,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    DatePipe,
    MatHeaderRow,
    MatRowDef,
    MatPaginator,
    NgTemplateOutlet,
    MatRow,
    MatHeaderRowDef,
    MatSortHeader,
    MatSort,
    NgxSkeletonLoaderComponent,
    MatCheckbox
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: GermanPaginatorIntl }],
  templateUrl: './generic-table.component.html',
  styleUrl: './generic-table.component.scss'
})
/**
 * Begründung: Hierbei handelt es sich um eine generische Tabellenkomponente.
 */
export class GenericTableComponent<T> implements AfterViewInit, OnChanges {
  protected readonly testIds = GenericTableTestIds;
  // Die Zeilen die angezeigt werden sollen
  columnsToDisplay = input.required<TableColumn<T>[]>();
  // Die Zeilen die existieren und tendenziell angezeigt werden können
  dataSource = input.required<T[]>();
  data = new MatTableDataSource<T>([]);
  expandedTemplate = input<TemplateRef<{ $implicit: T }>>();
  pageSize = input(10);
  pageIndex = input(0);
  totalItems = input(0);
  isLoading = input(false);
  activeSortKey = input('');
  showPaging = input(true);
  rowCheckboxClick = output<{ element: T; isChecked: boolean }>();
  rowClick = output<{ element: T }>();
  paginator = viewChild.required(MatPaginator);
  sort = viewChild.required(MatSort);
  pageChange = output<{ pageIndex: number; pageSize: number }>();
  sortChange = output<{ dir: string }>();
  expandedRow = signal<T | null>(null);
  pageSizeOptions = [5, 10, 20];
  skeletonData = new Array(20).fill({});
  clickedCheckboxRows = new Set<T>();
  clickedRow: T | null | undefined;
  showFirstLastButtons = true;
  displayedColumnsSignal = signal<TableColumn<T>[]>([]);

  get displayedColumns(): string[] {
    return this.displayedColumnsSignal().map(column => column.key);
  }

  get columnsToDisplayExpanded(): string[] {
    return [...this.displayedColumns, 'expandedDetail'];
  }

  ngAfterViewInit(): void {
    this.data.sort = this.sort();
    const paginator = this.paginator();
    if (paginator) {
      paginator.pageSize = this.pageSize();
      paginator.pageIndex = this.pageIndex();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource']) {
      this.data.data = this.dataSource() ?? [];
    }
    if (changes['columnsToDisplay']) {
      this.displayedColumnsSignal.set(this.columnsToDisplay());
    }
  }

  toggleRow(row: T): void {
    this.rowClick.emit({ element: row });
    if (this.expandedRow() === row) {
      this.clickedRow = null;
      this.expandedRow.set(null);
    } else {
      this.clickedRow = null;
      this.expandedRow.set(null);
      setTimeout(() => {
        this.expandedRow.set(row);
        this.clickedRow = row;
      }, 0);
    }
  }

  isExpanded(element: T): boolean {
    return this.expandedRow() === element;
  }

  emitRow(row: T, event: MatCheckboxChange): void {
    if (event.checked) {
      this.clickedCheckboxRows.add(row);
    } else {
      this.clickedCheckboxRows.delete(row);
    }
    this.rowCheckboxClick.emit({ element: row, isChecked: event.checked });
  }

  paginatorChange(event: PageEvent): void {
    this.pageChange.emit({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize
    });
  }

  onSortChange(event: Sort): void {
    this.data.paginator?.firstPage();
    this.sortChange.emit({
      dir: event.direction
    });
  }
}
